import { supabase } from '../lib/supabase'
import { currentRows, newestStamp } from './mirrorFreshness'

export { fetchShootEventIds } from './shootEventIds'

/**
 * The company Google calendar, shown beside Portal OS's own shoot days.
 *
 * Read-only, and only ever read. Portal OS owns shoot dates; the calendar
 * shows them. Displaying Google's events next to ours is not the two-way
 * sync that was rejected — nothing here can create or change a shoot.
 *
 * The browser never talks to Google: no Google credential can live in a
 * front end, so the calendar task copies the events into `calendar_mirror`
 * server-side and this reads that table. Which means the grid is as fresh as
 * the last run — every three hours in working hours since 16 Sep 2026 — so
 * the page says when it last ran rather than letting anyone assume otherwise.
 *
 * Rows the last couple of sweeps did not touch are dropped here rather than
 * deleted by the worker; utils/mirrorFreshness.ts explains why, and holds the
 * one number that has to move whenever that schedule changes.
 *
 * Staff-only by RLS. The company diary must never reach a client link.
 */

export interface MirroredEvent {
  eventId: string
  summary: string
  location: string
  allDay: boolean
  startDate: string
  endDate: string
  startTime: string
  htmlLink: string
  notProduction: boolean
}

/** Local-date ISO. Never toISOString(), which shifts the day near midnight. */
function isoOf(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

function eventOf(row: Record<string, unknown>, notProduction: string[]): MirroredEvent | null {
  const allDay = Boolean(row.all_day)
  const startAt = text(row.start_at)
  const endAt = text(row.end_at)

  const start = allDay
    ? text(row.start_date).slice(0, 10)
    : startAt
      ? isoOf(new Date(startAt))
      : ''
  if (!start) return null

  const end = allDay ? text(row.end_date).slice(0, 10) || start : endAt ? isoOf(new Date(endAt)) : start

  const summary = text(row.summary).trim() || '(untitled)'
  const haystack = summary.toLowerCase()

  return {
    eventId: text(row.event_id),
    summary,
    location: text(row.location),
    allDay,
    startDate: start,
    endDate: end < start ? start : end,
    startTime:
      allDay || !startAt
        ? ''
        : new Date(startAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    htmlLink: text(row.html_link),
    notProduction: notProduction.some((pattern) => pattern && haystack.includes(pattern)),
  }
}

/**
 * Titles that are never a shoot — the weekly radio slot, the internal
 * meeting, travel.
 *
 * Suppression only. This list can stop something being mistaken for
 * production; it can never make an unlisted entry INTO production, and
 * nothing should ever read it that way. An empty result is not a failure:
 * everything simply stays unclassified, which is the safe direction.
 */
async function fetchNotProductionPatterns(): Promise<string[]> {
  if (!supabase) return []
  const { data, error } = await supabase.from('calendar_not_production').select('pattern')
  if (error) {
    console.warn(`[supabase] not-production list load failed: ${error.message}`)
    return []
  }
  return (data ?? [])
    .map((row) => text((row as Record<string, unknown>).pattern).toLowerCase().trim())
    .filter(Boolean)
}

export interface MirrorSnapshot {
  events: MirroredEvent[]
  mirroredAt: number | null
}

/**
 * Every mirrored event overlapping the given date range, inclusive.
 *
 * Returns an empty list rather than throwing when the read is refused — a
 * calendar that cannot reach the mirror should still show Portal OS's own
 * shoots, which are the part that matters.
 */
export async function fetchMirroredEvents(fromIso: string, toIso: string): Promise<MirrorSnapshot> {
  if (!supabase) return { events: [], mirroredAt: null }

  const [result, notProduction] = await Promise.all([
    supabase
      .from('calendar_mirror')
      .select(
        'event_id, summary, location, all_day, start_date, end_date, start_at, end_at, html_link, mirrored_at',
      )
      .limit(500),
    fetchNotProductionPatterns(),
  ])

  if (result.error) {
    console.warn(`[supabase] calendar mirror load failed: ${result.error.message}`)
    return { events: [], mirroredAt: null }
  }

  const rows = (result.data ?? []) as Record<string, unknown>[]
  const stampOf = (row: Record<string, unknown>): number =>
    new Date(text(row.mirrored_at)).getTime()

  /* Filtered here rather than in the query: the range test is an OVERLAP
     across two different shapes of row (all-day dates, timed timestamps),
     and expressing that as one PostgREST filter is far easier to get subtly
     wrong than to just read the window — which is at most a few hundred
     rows, because the worker only ever mirrors 30 days back to 120 forward. */
  const events = currentRows(rows, stampOf)
    .map((row) => eventOf(row, notProduction))
    .filter((event): event is MirroredEvent => event !== null)
    .filter((event) => event.startDate <= toIso && event.endDate >= fromIso)

  return { events, mirroredAt: newestStamp(rows, stampOf) }
}
